import React, { Fragment } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Icon,
  Tooltip,
  IconButton,
  Popover,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { ChevronRight } from "@mui/icons-material";
import type { NavItem } from "./mainMenu.utils";
import { INACTIVE_COLOR } from "./mainMenu.utils";
import { prefetchLocationMap } from "../pages/locationMap/prefetchLocationMap";

interface CollapsedNavItemProps {
  nav: NavItem;
  isMobile: boolean;
  onMobileClose?: () => void;
  dropdownAnchor: Record<string, HTMLElement | null>;
  onDropdownAnchorChange: (key: string, el: HTMLElement | null) => void;
}

const CollapsedNavItem: React.FC<CollapsedNavItemProps> = ({
  nav,
  isMobile,
  onMobileClose,
  dropdownAnchor,
  onDropdownAnchorChange,
}) => {
  const location = useLocation();

  if (!nav.isParent) {
    const isActive = nav.path ? location.pathname.startsWith(nav.path) : false;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", gap: '8px' }}>
        <Tooltip title={nav.text} arrow placement="right">
          <IconButton
            component={RouterLink}
            to={nav.path || "/"}
            onClick={() => {
              if (isMobile && onMobileClose) onMobileClose();
            }}
            onMouseEnter={() => {
              if (nav.path === "/locationmap") prefetchLocationMap();
            }}
            sx={{
              borderRadius: "8px",
              backgroundColor: isActive ? "#3b82f6" : "transparent",
              color: isActive ? "#fff" : INACTIVE_COLOR,
              "&:hover": { backgroundColor: "#3b82f6", color: "#fff" },
              my: 0.25,
              width: "36px",
              height: "36px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon fontSize="small" className="material-symbols-outlined" sx={{ fontSize: "18px" }}>
              {nav.Icon}
            </Icon>
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Parent item with popover children
  const isParentActive =
    nav.children?.some((child) => location.pathname.startsWith(child.path || "")) ?? false;

  return (
    <Fragment>
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <Tooltip title={nav.text} arrow placement="right">
          <IconButton
            onClick={(e) => onDropdownAnchorChange(nav.text, e.currentTarget)}
            sx={{
              borderRadius: "8px",
              backgroundColor: isParentActive ? "#3b82f6" : "transparent",
              color: isParentActive ? "#fff" : INACTIVE_COLOR,
              "&:hover": { backgroundColor: "#3b82f6", color: "#fff" },
              position: "relative",
              width: "40px",
              height: "40px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon fontSize="small" className="material-symbols-outlined" sx={{ fontSize: "20px" }}>
              {nav.Icon}
            </Icon>
            <ChevronRight
              sx={{
                position: "absolute",
                right: "-12px",
                fontSize: "16px",
                color: "inherit",
                opacity: 0.8,
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Popover
        open={Boolean(dropdownAnchor[nav.text])}
        anchorEl={dropdownAnchor[nav.text]}
        onClose={() => onDropdownAnchorChange(nav.text, null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { bgcolor: "black", color: "white", minWidth: 200, mt: 0.5 },
        }}
      >
        <List dense>
          {nav.children?.map((child) => {
            const isChildActive = location.pathname.startsWith(child.path || "");
            return (
              <ListItemButton
                key={child.path}
                component={RouterLink}
                to={child.path || "/"}
                onClick={() => {
                  onDropdownAnchorChange(nav.text, null);
                  if (isMobile && onMobileClose) onMobileClose();
                }}
                sx={{
                  color: isChildActive ? "#fff" : "rgba(255, 255, 255, 0.72)",
                  bgcolor: isChildActive ? "#3b82f6" : "transparent",
                  "&:hover": { bgcolor: "#3b82f6", color: "#fff" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                  <Icon fontSize="small" className="material-symbols-outlined">
                    {child.Icon}
                  </Icon>
                </ListItemIcon>
                <ListItemText
                  primary={child.text}
                  primaryTypographyProps={{ sx: { color: "inherit", fontSize: "14px" } }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </Fragment>
  );
};

export default CollapsedNavItem;
